/**
 * Error Handler - Centralized error classes and transformation utilities
 * Provides typed errors for different failure scenarios
 */

/**
 * Base API Error class
 * @class ApiError
 * @extends Error
 */
export class ApiError extends Error {
  /**
   * @param {string} message - Error message
   * @param {number} statusCode - HTTP status code
   * @param {Object} [details] - Additional error details from API response
   */
  constructor(message, statusCode, details = null) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.details = details;
    this.isApiError = true;
  }
}

/**
 * Validation Error class - thrown when API response doesn't match expected schema
 * @class ValidationError
 * @extends Error
 */
export class ValidationError extends Error {
  /**
   * @param {string} message - Error message
   * @param {Object} [details] - Validation details
   */
  constructor(message, details = null) {
    super(message);
    this.name = "ValidationError";
    this.details = details;
    this.isValidationError = true;
  }
}

/**
 * Authentication Error class - thrown for 401/403 responses
 * @class AuthenticationError
 * @extends ApiError
 */
export class AuthenticationError extends ApiError {
  /**
   * @param {string} message - Error message
   * @param {number} statusCode - HTTP status code (401 or 403)
   * @param {Object} [details] - Additional error details
   */
  constructor(message, statusCode = 401, details = null) {
    super(message, statusCode, details);
    this.name = "AuthenticationError";
    this.isAuthenticationError = true;
  }
}

/**
 * Network Error class - thrown when network request fails
 * @class NetworkError
 * @extends Error
 */
export class NetworkError extends Error {
  /**
   * @param {string} message - Error message
   * @param {Error} [originalError] - Original error object
   */
  constructor(message, originalError = null) {
    super(message);
    this.name = "NetworkError";
    this.originalError = originalError;
    this.isNetworkError = true;
  }
}

/**
 * Transform API error response into typed error
 * @param {Error} error - Error object from API call
 * @param {Object} response - API response object (if available)
 * @returns {ApiError|AuthenticationError|NetworkError|ValidationError}
 */
export function transformApiError(error, response = null) {
  // Network error (no response received)
  if (!response) {
    if (error.message === "Network Error" || error.message?.includes("ECONNREFUSED")) {
      return new NetworkError("Network connection failed. Please check your internet connection.", error);
    }
    return new NetworkError(error.message || "Unknown network error", error);
  }

  const statusCode = response.status;
  const errorData = response.data;
  const errorMessage = errorData?.error?.message || errorData?.message || "API request failed";

  // Authentication errors
  if (statusCode === 401) {
    return new AuthenticationError("Session expired. Please login again.", statusCode, errorData);
  }

  if (statusCode === 403) {
    return new AuthenticationError("You don't have permission to perform this action.", statusCode, errorData);
  }

  // Client errors (4xx)
  if (statusCode >= 400 && statusCode < 500) {
    return new ApiError(errorMessage, statusCode, errorData);
  }

  // Server errors (5xx)
  if (statusCode >= 500) {
    return new ApiError("Server error. Please try again later.", statusCode, errorData);
  }

  // Generic API error
  return new ApiError(errorMessage, statusCode, errorData);
}

/**
 * Get user-friendly error message
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
  if (error.isAuthenticationError) {
    return error.message;
  }

  if (error.isNetworkError) {
    return error.message;
  }

  if (error.isApiError) {
    return error.message;
  }

  if (error.isValidationError) {
    return "Invalid response from server. Please try again.";
  }

  return error.message || "An unexpected error occurred.";
}
